import { useState, useEffect } from 'react'
import i18n from '../../../i18n'
import { bruktbilfinansieringAPI } from '../../../services/api'
import type { DealerSoldRowsResponse } from '../../../types/bruktbilfinansiering'

interface Filters {
  startDate?: string
  endDate?: string
  varebil?: boolean
  classes?: string[]
  dealerPostnummer?: string
  buyerPostnummer?: string
}

/** Rows for the "Uten finansieringsdata" tab (#407).
 *
 *  `enabled` is what keeps this off the page-load path: the grid can run to
 *  several thousand rows, and most visits never open the tab. Nothing is
 *  fetched until the tab is actually selected, and the result is kept after
 *  the user switches away so toggling back is free.
 */
export function useDealerSoldRows(
  bankOrgNr: string,
  dealerOrgNr: string,
  filters: Filters,
  enabled: boolean,
) {
  const [data, setData] = useState<DealerSoldRowsResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const classesKey = (filters.classes ?? []).join(',')

  useEffect(() => {
    if (!enabled || !bankOrgNr || !dealerOrgNr) return
    let cancelled = false
    setLoading(true)
    setError(null)
    bruktbilfinansieringAPI.getDealerSoldRows(bankOrgNr, dealerOrgNr, {
      start_date: filters.startDate,
      // An undefined end_date is meaningful, not missing: the backend then
      // fills in MAX(hendelse_tidspunkt). The caller decides when to omit it —
      // see resolveSoldRowsEndDate in DealerDetailsPage.
      end_date: filters.endDate,
      varebil: filters.varebil,
      classes: filters.classes,
      dealer_postnummer: filters.dealerPostnummer,
      buyer_postnummer: filters.buyerPostnummer,
    })
      .then(res => { if (!cancelled) setData(res.data) })
      .catch(e => { if (!cancelled) setError(e instanceof Error ? e.message : i18n.t('errors.soldRows', { ns: 'bbf' })) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, bankOrgNr, dealerOrgNr, filters.startDate, filters.endDate, filters.varebil,
      classesKey, filters.dealerPostnummer, filters.buyerPostnummer])

  return { data, loading, error }
}
